import {
  NestedComponentStyleOverrides,
  StyleBindings,
  StyleClassDefinitionsByComponentId,
} from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { isEqual, pickBy } from 'lodash/fp';
import ComponentInstanceProxy from '../composer/dolanruntime/ComponentInstanceProxy';
import ViewProxy from '../composer/dolanruntime/ViewProxy';
import { StyleBinding } from '../composer/styles/style-binding';
import { getNestedOverridesKey } from './components';
import traverseComponent from '../composer/components/utils/traverseComponent';
import { ComponentName } from './design-system';
import { layoutStylePropertyNames, paddingPropertyNames, StyleProperty } from './style-properties';
import { migrateStyles } from './styles';

export const removeIdenticalAppStyleClasses = (
  appStyleClasses: StyleClassDefinitionsByComponentId = {},
  themeStyleClasses: StyleClassDefinitionsByComponentId = {},
) =>
  Object.keys(appStyleClasses).reduce((result: StyleClassDefinitionsByComponentId, componentId: string) => {
    if (!isEqual(themeStyleClasses[componentId], appStyleClasses[componentId])) {
      return {
        ...result,
        [componentId]: appStyleClasses[componentId],
      } as StyleClassDefinitionsByComponentId;
    }
    return result;
  }, {});

export const mergeStyleClasses = (
  appStyleClasses: StyleClassDefinitionsByComponentId = {},
  themeStyleClasses: StyleClassDefinitionsByComponentId = {},
  componentTemplateStyleClasses: StyleClassDefinitionsByComponentId = {},
) => {
  const mergedStyleClasses: StyleClassDefinitionsByComponentId = {};
  const addComponentStyleClass = (componentId: string) => {
    if (mergedStyleClasses[componentId] == null) {
      // eslint-disable-next-line prefer-object-spread
      mergedStyleClasses[componentId] = Object.assign(
        {},
        componentTemplateStyleClasses[componentId],
        themeStyleClasses[componentId],
        appStyleClasses[componentId],
      );
    }
  };
  Object.keys(appStyleClasses).forEach(addComponentStyleClass);
  Object.keys(themeStyleClasses).forEach(addComponentStyleClass);
  Object.keys(componentTemplateStyleClasses).forEach(addComponentStyleClass);
  return mergedStyleClasses;
};

/**
 * Selectively resets an inline style object or a nested override style object
 * using a blacklist and a list of valid theme variables and palette names
 */
export const resetStyleObject = (
  style: StyleBindings,
  isLayoutContainer: boolean,
  whitelistedThemeVariables: string[],
  whitelistedPalettes: string[],
) => {
  if (!style) {
    return style;
  }
  const migratedStyles = migrateStyles(style);
  return pickBy((_styleValue, styleProperty) => {
    // pick layout style properties, and also padding properties in the case of layout container
    if (
      !layoutStylePropertyNames.includes(styleProperty as StyleProperty) &&
      !(isLayoutContainer && paddingPropertyNames.includes(styleProperty as StyleProperty))
    ) {
      return false;
    }
    const migratedStyleValue = migratedStyles?.[styleProperty as StyleProperty] as StyleBinding;
    if (!migratedStyleValue || !migratedStyleValue?.source) {
      return true;
    }
    if (migratedStyleValue.source === 'smart-content-color') {
      // @ts-ignore
      return whitelistedPalettes.includes(migratedStyleValue?.value);
    }
    if (migratedStyleValue.source === 'theme') {
      return whitelistedThemeVariables.includes(
        // @ts-ignore
        migratedStyleValue?.key || migratedStyleValue?.value,
      );
    }
    if (migratedStyleValue.source === 'smart-background') {
      // @ts-ignore
      return whitelistedThemeVariables.includes(migratedStyleValue?.value?.themeVariable);
    }
    return true;
  }, style);
};

const setFallbackStyleClassToNestedOverrides = (
  compositeNestedOverrides: NestedComponentStyleOverrides | undefined,
  nestedOverrideKey: string,
  styleClass: string,
) => {
  if (!compositeNestedOverrides?.[nestedOverrideKey]?.style_class) {
    return compositeNestedOverrides;
  }
  return {
    ...compositeNestedOverrides,
    [nestedOverrideKey]: {
      ...compositeNestedOverrides[nestedOverrideKey],
      style_class: styleClass,
    },
  };
};

const setResetStyleToNestedOverrides = (
  compositeNestedOverrides: NestedComponentStyleOverrides | undefined,
  nestedOverrideKey: string,
  isLayoutContainer: boolean,
  resetInstanceStyles: (style: StyleBindings, isLayoutContainer: boolean) => StyleBindings,
) => {
  if (!compositeNestedOverrides?.[nestedOverrideKey]?.style) {
    return compositeNestedOverrides;
  }
  return {
    ...compositeNestedOverrides,
    [nestedOverrideKey]: {
      ...compositeNestedOverrides[nestedOverrideKey],
      style: resetInstanceStyles(compositeNestedOverrides?.[nestedOverrideKey]?.style ?? {}, isLayoutContainer),
    },
  };
};

const handleComponentInstanceReset = (
  component: ComponentInstanceProxy,
  appStyleClasses: StyleClassDefinitionsByComponentId,
  validStyleClasses: string[],
  fallbackStyleClass: string,
  resetInstanceStyles?: (style: StyleBindings, isLayoutContainer: boolean) => StyleBindings,
) => {
  const isComponentCompositeRoot = !!component.description.composite_json;
  const compositeRoot = component.getCompositeRoot();
  const compositeToReset = isComponentCompositeRoot ? component : compositeRoot;
  const nestedOverridesKey = getNestedOverridesKey(component);
  const hasThemeDefinedStyleClasses = !!validStyleClasses.length;
  const layoutContainerStyleClass = 'layout';
  if (
    !compositeRoot &&
    component.style_class &&
    hasThemeDefinedStyleClasses &&
    !validStyleClasses.includes(component.style_class)
  ) {
    component.style_class = fallbackStyleClass; // eslint-disable-line no-param-reassign
  }
  if (!compositeRoot && component.style_class && resetInstanceStyles) {
    const isLayoutContainer =
      component.name === ComponentName.Container && component.style_class === layoutContainerStyleClass;
    // eslint-disable-next-line no-param-reassign
    component.style = resetInstanceStyles(component.style, isLayoutContainer);
  }

  if (component.nested_overrides && hasThemeDefinedStyleClasses) {
    component.nested_overrides = undefined; // eslint-disable-line no-param-reassign
  }

  if (!compositeToReset) {
    return;
  }

  // check inline nested overrides of the composite to reset
  if (compositeToReset?.nested_overrides) {
    const nestedOverride = nestedOverridesKey ? compositeToReset.nested_overrides?.[nestedOverridesKey] : undefined;
    const shouldUpdateNestedOverridesStyleClass =
      nestedOverridesKey &&
      nestedOverride?.style_class &&
      hasThemeDefinedStyleClasses &&
      !validStyleClasses.includes(nestedOverride?.style_class);
    if (shouldUpdateNestedOverridesStyleClass) {
      compositeToReset.nested_overrides = setFallbackStyleClassToNestedOverrides(
        compositeToReset.nested_overrides,
        nestedOverridesKey,
        fallbackStyleClass,
      );
    }
    const shouldUpdateNestedOverridesStyle = nestedOverridesKey && nestedOverride?.style && resetInstanceStyles;
    if (shouldUpdateNestedOverridesStyle) {
      const isLayoutContainer =
        component.name === ComponentName.Container && nestedOverride?.style_class === layoutContainerStyleClass;
      compositeToReset.nested_overrides = setResetStyleToNestedOverrides(
        compositeToReset.nested_overrides,
        nestedOverridesKey,
        isLayoutContainer,
        resetInstanceStyles,
      );
    }
  }

  // check app level style classes of the composite to reset
  if (appStyleClasses[compositeToReset.name]) {
    Object.keys(appStyleClasses[compositeToReset.name]).forEach((styleClassId) => {
      const styleClassDefinition = appStyleClasses[compositeToReset.name][styleClassId];
      const nestedOverride = nestedOverridesKey
        ? styleClassDefinition?.nested_overrides?.[nestedOverridesKey]
        : undefined;
      const shouldUpdateNestedOverridesStyleClass =
        nestedOverridesKey && nestedOverride?.style_class && !validStyleClasses.includes(nestedOverride?.style_class);
      if (shouldUpdateNestedOverridesStyleClass) {
        styleClassDefinition.nested_overrides = setFallbackStyleClassToNestedOverrides(
          styleClassDefinition.nested_overrides,
          nestedOverridesKey,
          fallbackStyleClass,
        );
      }
      const shouldUpdateNestedOverridesStyle = nestedOverridesKey && nestedOverride?.style && resetInstanceStyles;
      if (shouldUpdateNestedOverridesStyle) {
        const isLayoutContainer =
          component.name === ComponentName.Container && nestedOverride?.style_class === layoutContainerStyleClass;
        styleClassDefinition.nested_overrides = setResetStyleToNestedOverrides(
          styleClassDefinition.nested_overrides,
          nestedOverridesKey,
          isLayoutContainer,
          resetInstanceStyles,
        );
      }
    });
  }
};

/**
 * For a component name, it iterates over pages,
 * finds the instances of the component and the composite root of the component
 * checks if component, composite root inline nested overrides or app level style classes
 * for the composite root define an invalid style class, and
 * replaces it with a fallback style class
 */
export const resetStyleClassesForInstances = (
  pages: ViewProxy[],
  componentName: string,
  appStyleClasses: StyleClassDefinitionsByComponentId,
  validStyleClasses: string[],
  fallbackStyleClass: string,
  resetInstanceStyles?: (style: StyleBindings, isLayoutContainer: boolean) => StyleBindings,
) => {
  pages.forEach((page) => {
    traverseComponent(page.layout.root_component, (component: ComponentInstanceProxy) => {
      if (component.name === componentName) {
        handleComponentInstanceReset(
          component,
          appStyleClasses,
          validStyleClasses,
          fallbackStyleClass,
          resetInstanceStyles,
        );
      }
    });
  });
};
